// Which company files (W-9, insurance certificate) the share/request credentials dialog offers,
// and how the ticked ones travel in the body. The server decides who may share what - only the
// company admin may share a file, and only one the company holds - so this is convenience only.

import React from 'react';
import { FormattedMessage } from '../../util/reactIntl';

// The company files, in the order they are shown.
export const COMPANY_FILE_KINDS = ['w9', 'insurance'];

// The verification rows about the company itself. Rows about a person (owners, authorized persons,
// identity) never offer company files.
const COMPANY_ROWS = [
  'business-identity',
  'businessIdentity',
  'businessRegistry',
  'businessOther',
  'insurance-reports',
];

const inDisplayOrder = kinds => COMPANY_FILE_KINDS.filter(kind => kinds.includes(kind));

// The files the company holds, known only once its own credentials are loaded. Until then nothing
// is claimed to be held.
export const heldCompanyFiles = ({ loaded, companyCredentials }) => {
  if (!loaded) return [];
  const links = {
    w9: companyCredentials?.companyIdentification?.w9FormUrl,
    insurance: companyCredentials?.insuranceCertificateUrl,
  };
  return COMPANY_FILE_KINDS.filter(kind => !!links[kind]);
};

// A share offers only what the company holds. A request offers both files whatever the company
// holds, so the dialog never reveals which files exist.
export const offeredCompanyFiles = ({ listingIsAgency, columnName, isShare, heldFiles = [] }) => {
  if (!listingIsAgency || !COMPANY_ROWS.includes(columnName)) return [];
  return isShare ? inDisplayOrder(heldFiles) : [...COMPANY_FILE_KINDS];
};

export const toggleCompanyFile = (ticked, kind) =>
  ticked.includes(kind) ? ticked.filter(k => k !== kind) : inDisplayOrder([...ticked, kind]);

// With nothing ticked the body goes out exactly as it did before files existed.
export const withCompanyFiles = (body, ticked) =>
  ticked.length > 0 ? { ...body, files: [...ticked] } : body;

export const dialogCanSubmit = ({ fields, files }) => fields.length > 0 || files.length > 0;

// Rendered inside the dialog with its own CSS module so the file rows match the field rows.
export const CompanyFileRows = ({ files, ticked, onToggle, css }) => {
  if (!files || files.length === 0) return null;

  return (
    <div style={{ marginTop: '16px' }}>
      <h4 className={css.requestDocumentModalSectionTitle}>
        <FormattedMessage id="CompanyFile.heading" />
      </h4>
      {files.map(kind => (
        <div key={kind} className={css.requestFieldCheckboxItem}>
          <input
            type="checkbox"
            id={`company-file-${kind}`}
            checked={ticked.includes(kind)}
            onChange={() => onToggle(kind)}
            className={css.requestFieldCheckbox}
          />
          <label htmlFor={`company-file-${kind}`} className={css.requestFieldLabel}>
            <FormattedMessage id={`CompanyFile.${kind}`} />
          </label>
        </div>
      ))}
    </div>
  );
};
